import { useIsAdmin } from '@/hooks/use-scopes';
import { FileName, FolderPath, Iso } from '@/components/ui/FileName';
import { PHASES, PHASE_NAMES, familyOf, phaseNames } from '@/lib/steps';
import StageView from '@/components/creative/StageView';
import { Link, useSearchParams } from 'react-router-dom';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Image as ImageIcon, Video, FileText, Layout, BookOpen, Camera, Type, File as FileIcon,
  Upload, Trash2, Star, Download, Play, ExternalLink, MessageSquare, Check, Loader2, Clapperboard, Copy,
} from 'lucide-react';
import { formatEta } from '@/lib/upload-center';
import { useDownloadCenter } from '@/lib/download-center';
import MaterialPicker from '@/components/creative/MaterialPicker';
import OtherCreativePicker from '@/components/creative/OtherCreativePicker';
import { toast } from 'sonner';
import { Card } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Select from '@/components/ui/Select';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import ReviewModal from '@/components/creative/ReviewModal';
import {
  fetchAssets, deleteAsset, patchAsset, fetchCreativeReviews, bulkAssets, createReview, addRawFolder, fetchCreativeTasks, fetchPhaseGate,
  RAW_SUGGESTIONS, FOLDER_KINDS, renameFolder,
  type Creative, type MediaAsset, type AssetKind, type AssetReview, type RawCategory,
} from '@/lib/queries';
import BulkUploadDialog from '@/components/creative/BulkUploadDialog';
import VoiceRecorder from '@/components/ui/VoiceRecorder';
import { StickyNote, FolderOpen, FolderPlus, Folder, ChevronLeft, PencilLine } from 'lucide-react';
import MentionTextarea from '@/components/creative/MentionTextarea';
import PromptDialog from '@/components/ui/PromptDialog';
import { CheckSquare, Square } from 'lucide-react';
import { useUploadCenter } from '@/lib/upload-center';
import { useIsStaff } from '@/hooks/use-role';
import { useCan } from '@/hooks/use-scopes';
import { useAuth } from '@/hooks/use-auth';
import { cn } from '@/lib/cn';
import { filesFromDataTransfer, droppedFolder, droppedFolders } from '@/lib/dropped';
import { isJunkFile, showAs, typeBadge } from '@/lib/file-types';
import { useCollapsed, CollapseChevron } from '@/components/ui/CollapsibleCard';

const KIND_ICON: Record<AssetKind, React.ReactNode> = {
  raw:        <Video size={12} />,
  edit:       <Video size={12} />,
  final:      <Play size={12} />,
  static:     <ImageIcon size={12} />,
  poster:     <ImageIcon size={12} />,
  reference:  <BookOpen size={12} />,
  brief_doc:  <FileText size={12} />,
  thumbnail:  <ImageIcon size={12} />,
};

// Badges sit on top of arbitrary preview backgrounds (white PDFs, bright images,
// dark videos), so the chip itself is solid near-black with a colored text+ring
// per kind. Readable everywhere.
const KIND_COLOR: Record<AssetKind, string> = {
  raw:        'bg-slate-950/85 text-blue-300    ring-1 ring-blue-400/60    shadow-md',
  edit:       'bg-slate-950/85 text-purple-300  ring-1 ring-purple-400/60  shadow-md',
  final:      'bg-slate-950/85 text-emerald-300 ring-1 ring-emerald-400/60 shadow-md',
  static:     'bg-slate-950/85 text-cyan-300    ring-1 ring-cyan-400/60    shadow-md',
  poster:     'bg-slate-950/85 text-pink-300    ring-1 ring-pink-400/60    shadow-md',
  reference:  'bg-slate-950/85 text-yellow-300  ring-1 ring-yellow-400/60  shadow-md',
  brief_doc:  'bg-slate-950/85 text-slate-200   ring-1 ring-slate-400/60   shadow-md',
  thumbnail:  'bg-slate-950/85 text-orange-300  ring-1 ring-orange-400/60  shadow-md',
};

const ALL_KINDS: AssetKind[] = ['raw', 'edit', 'final', 'static', 'poster', 'reference', 'brief_doc', 'thumbnail'];
//: the phases an edit goes through, in order, with the names people use for them
const EDIT_PHASES = PHASES;

export default function AssetsPanel({ creative, alwaysOpen = false }: { creative: Creative; /** Files shows it open, never folded */ alwaysOpen?: boolean }) {
  const qc = useQueryClient();
  const isStaff = useIsStaff();
  // Executors manage only their own uploads; leads and admins manage everything.
  const canWrite = useCan('creatives.write');
  const { user: me } = useAuth();
  const fileRef = useRef<HTMLInputElement>(null);
  const dirRef = useRef<HTMLInputElement>(null);
  const [activeKind, setActiveKind] = useState<AssetKind | 'all'>('all');
  const [uploadKind, setUploadKind] = useState<AssetKind>(() => suggestedKindFor(creative.creative_type));
  // a kind the person chose (the Upload as list, or a reference/static/poster/brief tab):
  // a dropped folder then keeps that kind instead of turning into raws
  const [kindChosen, setKindChosen] = useState(false);
  const chooseKind = (k: AssetKind) => { setUploadKind(k); setKindChosen(true); };
  // The edit is one phase at a time, so an edit upload files into the phase that is open
  // now; the picker is gone, that is how files landed in the wrong step.
  const editTasks = useQuery({ queryKey: ['tasks', 'creative', creative.id], queryFn: () => fetchCreativeTasks(creative.id), enabled: isStaff });
  const openPhase = (['rough', 'colour', 'final'] as const).find((p) => (editTasks.data ?? []).some((t) => t.stage === 'edit' && t.phase === p && !['done', 'cancelled'].includes(t.status))) ?? null;
  useEffect(() => { if (openPhase) setUploadPhase(openPhase); }, [openPhase]);
  const [uploadPhase, setUploadPhase] = useState<'rough' | 'colour' | 'final'>('rough');
  // one table of names per kind of work (lib/steps.ts): a photo's tabs, badges and "Move to
  // phase" say Selection, not Rough cut, the same words as the Work card (FE-14)
  const phaseLabels = phaseNames(creative.creative_type);
  const EDIT_PHASE_LABEL: Record<string, string> = phaseLabels;
  // One stage at a time (Aziz 2026-09-29): a stage opens once the one before it is
  // approved. Same rule as the server (api/app/phase_gate.py); owners and managers may override.
  // The server decides (GET /phase-gate): a tasks-level editor lists only their own tasks, so
  // working it out here locked Dalia's final edit behind a rough cut approved weeks before.
  const isAdminHere = useIsAdmin();
  const gate = useQuery({
    queryKey: ['phase-gate', creative.id, editTasks.dataUpdatedAt],
    queryFn: () => fetchPhaseGate(creative.id),
    enabled: isStaff,
  });
  const lockedWhy = (phase: 'rough' | 'colour' | 'final' | null): string | null => {
    if (!phase || phase === 'rough' || isAdminHere) return null;
    return gate.data?.[phase] ?? null;
  };
  const uploadTarget = uploadKind === 'edit' ? uploadPhase : uploadKind === 'final' ? 'final' : null;
  const uploadLocked = lockedWhy(uploadTarget);
  // One rule since 2026-10-30 (Aziz): an upload files into the stage the piece is at, and only an
  // admin chooses the kind. This mirrors upload_rules.filing_for, which decides on the server anyway.
  const strict = !isAdminHere;
  const nextEdit = openPhase ?? (['rough', 'colour'] as const).find((p) => !(editTasks.data ?? []).some((t) => t.stage === 'edit' && t.phase === p && t.status === 'done')) ?? 'colour';
  const stageFiling = filingFor(creative, nextEdit, phaseLabels);
  useEffect(() => {
    if (!strict || !stageFiling.kind) return;
    setUploadKind(stageFiling.kind);
    if (stageFiling.kind === 'edit') setUploadPhase(nextEdit);
  }, [strict, stageFiling.kind, nextEdit]); // eslint-disable-line react-hooks/exhaustive-deps
  // not this person's step (the server's own-step rule): said up front, before any file is picked
  const refusedHere = strict ? (stageFiling.refused ?? gate.data?._upload ?? null) : null;
  const [reviewing, setReviewing] = useState<MediaAsset | null>(null);
  // a notification about a file opens it here, at its comment (?asset=&review=&t=)
  const [params, setParams] = useSearchParams();
  const [focus, setFocus] = useState<{ review?: string; t?: number } | null>(null);
  // Aziz 2026-09-29: the files by stage and round are the default; All files keeps the
  // upload folders and the bulk tools one tap away
  const [view, setView] = useState<'stages' | 'files'>(() => {
    try { return (localStorage.getItem('assets.view') as 'stages' | 'files') || 'stages'; } catch { return 'stages'; }
  });
  const showView = (v: 'stages' | 'files') => { setView(v); try { localStorage.setItem('assets.view', v); } catch { /* private mode */ } };
  // the viewer's strip walks the round (or folder) the file was opened from
  const [strip, setStrip] = useState<MediaAsset[] | null>(null);
  const [pendingDelete, setPendingDelete] = useState<MediaAsset | null>(null);
  // Bulk selection: ids ticked in the grid, plus one confirm for bulk delete.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState<'' | 'zip' | 'delete' | 'kind'>('');
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [rekind, setRekind] = useState<AssetKind | ''>('');
  const [refolder, setRefolder] = useState<RawCategory | ''>('');
  // the second list that goes with a new kind: a raw's folder or an edit's phase
  const [rekindWhere, setRekindWhere] = useState('');
  // Raw folders (Mahmoud's list): a sub-filter inside the Raw tab.
  const [rawFolder, setRawFolder] = useState<RawCategory | 'all'>('all');
  const [uploadCategory, setUploadCategory] = useState<RawCategory>('Any');
  const [newFolder, setNewFolder] = useState(false);
  // Explorer selection. Separate from rawFolder, which is the folder that is OPEN.
  const [picked, setPicked] = useState<string>('__all__');
  const { open: foldOpen, toggle: togglePanel } = useCollapsed('creative.assets.open');
  const panelOpen = alwaysOpen || foldOpen;
  const coarse = useCoarsePointer();
  // Picking raws opens the filing wizard instead of uploading blind.
  const [wizardFiles, setWizardFiles] = useState<File[] | null>(null);
  const uploads = useUploadCenter();
  const [noteFor, setNoteFor] = useState<MediaAsset | null>(null);

  const reviewsQ = useQuery({
    queryKey: ['creative-reviews', creative.id],
    queryFn: () => fetchCreativeReviews(creative.id),
  });

  // Group reviews by asset id so each card can show its own count + preview without N+1 fetches
  const reviewsByAsset = useMemo(() => {
    const map = new Map<string, AssetReview[]>();
    for (const r of reviewsQ.data ?? []) {
      const arr = map.get(r.asset_id) ?? [];
      arr.push(r);
      map.set(r.asset_id, arr);
    }
    return map;
  }, [reviewsQ.data]);

  const assets = useQuery({
    queryKey: ['assets', creative.id],
    queryFn: () => fetchAssets(creative.id),
    refetchInterval: (q) =>
      (q.state.data ?? []).some((a) => a.proxy_status === 'queued' || a.proxy_status === 'pending') ? 20000 : false,
  });
  // A version deleted after it was sent for review still belongs to its round (Aziz
  // 2026-10-30: Sara could not see earlier rounds; 21 of 119 rounds read empty).
  const archived = useQuery({
    queryKey: ['assets', creative.id, 'archived'],
    queryFn: () => fetchAssets(creative.id, undefined, true),
    enabled: isStaff && view === 'stages',
  });
  const stageAssets = useMemo(() => {
    const live = assets.data ?? [];
    const ids = new Set(live.map((a) => a.id));
    return [...live, ...(archived.data ?? []).filter((a) => a.active === false && !ids.has(a.id))];
  }, [assets.data, archived.data]);

  useEffect(() => {
    const aid = params.get('asset');
    if (!aid || !assets.data) return;
    const a = assets.data.find((x) => x.id === aid);
    if (a) {
      setFocus({ review: params.get('review') ?? undefined, t: params.get('t') ? Number(params.get('t')) : undefined });
      setStrip(null); setReviewing(a);
    }
    const next = new URLSearchParams(params); next.delete('asset'); next.delete('review'); next.delete('t');
    setParams(next, { replace: true });
  }, [params, assets.data]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Counts per kind for the subtabs
  const counts: Record<string, number> = { all: assets.data?.length ?? 0 };
  for (const a of assets.data ?? []) counts[a.kind] = (counts[a.kind] ?? 0) + 1;

  // Raws are bulk working material and a shoot leaves hundreds of them. Nothing raw
  // is rendered until a folder is actually OPEN, so landing on a creative never
  // pulls a wall of thumbnails, on the All tab either. The folders carry the counts.
  const rawFolderOpen = activeKind === 'raw' && rawFolder !== 'all';
  // References, statics, posters and brief documents have folders too (2026-10-15): the
  // tab of such a kind shows its folders as tiles above its loose files, and `rawFolder`
  // is then the folder open in THAT kind. A raw with no folder is in "Any"; any other
  // file with no folder is loose, at the top level.
  const folderKind: AssetKind | null = activeKind !== 'all' && FOLDER_KINDS.includes(activeKind) ? activeKind : null;
  const fk: AssetKind = folderKind ?? 'raw';
  const catOf = (a: MediaAsset): string | null => (a.kind === 'raw' ? (a.category ?? 'Any') : (a.category || null));
  const openTab = (k: AssetKind | 'all') => {
    setActiveKind(k); setRawFolder('all'); setPicked('__all__');
    if (k !== 'all' && k !== 'raw' && FOLDER_KINDS.includes(k)) chooseKind(k);
  };
  // The edit phases each get their own folder. They all share kind='edit', so before
  // this the rough cuts, the colour passes and the inserts sat in one pile and the
  // person approving could not tell which file the task meant.
  const [editPhase, setEditPhase] = useState<'all' | 'rough' | 'colour' | 'final'>('all');
  const editPhaseOpen = activeKind === 'edit' && editPhase !== 'all';
  const [favOnly, setFavOnly] = useState(false);
  const [pickingMaterial, setPickingMaterial] = useState(false);
  const [pickingOther, setPickingOther] = useState(false);
  // which kinds are in the selection: the folder or phase list shows when they are all one kind
  const selectedKinds = useMemo(() => new Set((assets.data ?? []).filter((a) => selected.has(a.id)).map((a) => a.kind)), [assets.data, selected]);
  const visible = (assets.data ?? []).filter((a) => (!favOnly || a.is_favorite) && (activeKind === 'all' || a.kind === activeKind)
    && (a.kind !== 'raw' || (rawFolderOpen && (a.category ?? 'any') === rawFolder))
    && (a.kind === 'raw' || !folderKind || a.kind !== folderKind || (rawFolder === 'all' ? !catOf(a) : catOf(a) === rawFolder))
    && (a.kind !== 'edit' || !editPhaseOpen || (a.category ?? '') === editPhase));
  const editCounts: Record<string, number> = { rough: 0, colour: 0, final: 0 };
  for (const a of assets.data ?? []) if (a.kind === 'edit' && a.category && a.category in editCounts) editCounts[a.category] += 1;
  const rawCounts: Record<string, number> = {};
  for (const a of assets.data ?? []) if (a.kind === 'raw') rawCounts[a.category ?? 'Any'] = (rawCounts[a.category ?? 'Any'] ?? 0) + 1;
  // the same counts for the folder kind whose tab is open (raws when none is)
  const kindCounts: Record<string, number> = {};
  for (const a of assets.data ?? []) { const c = a.kind === fk ? catOf(a) : null; if (c) kindCounts[c] = (kindCounts[c] ?? 0) + 1; }
  // Folders = the team's own names on this creative, plus any folder a file already sits in.
  const folders = useMemo(() => {
    const seen = new Map<string, string>();
    for (const f of [...(creative.raw_folders ?? []), ...Object.keys(rawCounts)]) if (!seen.has(f.toLowerCase())) seen.set(f.toLowerCase(), f);
    return Array.from(seen.values());
  }, [creative.raw_folders, assets.data]);   // eslint-disable-line react-hooks/exhaustive-deps
  // the folders of the kind whose tab is open; raws add the names the team made empty
  const kindFolders = useMemo(() => {
    if (fk === 'raw') return folders;
    const seen = new Map<string, string>();
    for (const f of Object.keys(kindCounts)) if (!seen.has(f.toLowerCase())) seen.set(f.toLowerCase(), f);
    return Array.from(seen.values()).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  }, [fk, folders, assets.data]);   // eslint-disable-line react-hooks/exhaustive-deps
  const folderOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const f of [...folders, ...RAW_SUGGESTIONS]) if (!seen.has(f.toLowerCase())) seen.set(f.toLowerCase(), f);
    return Array.from(seen.values()).map((v) => ({ value: v, label: v, hint: folders.includes(v) ? 'on this creative' : undefined }));
  }, [folders]);
  // Folders nest by path: "Day 1/Dr Sara" sits inside "Day 1". A parent that was never
  // named on its own still shows, because its children need somewhere to hang.
  const parentOf = (f: string) => (f.includes('/') ? f.slice(0, f.lastIndexOf('/')) : 'all');
  const leafOf = (f: string) => (f.includes('/') ? f.slice(f.lastIndexOf('/') + 1) : f);
  const childrenOf = (parent: string) => {
    const seen = new Map<string, string>();
    for (const f of kindFolders) {
      const rel = parent === 'all' ? f : (f.toLowerCase().startsWith(parent.toLowerCase() + '/') ? f.slice(parent.length + 1) : null);
      if (rel === null) continue;
      const head = rel.split('/')[0];
      const full = parent === 'all' ? head : `${parent}/${head}`;
      if (!seen.has(full.toLowerCase())) seen.set(full.toLowerCase(), full);
    }
    return Array.from(seen.values());
  };
  // a folder's count is everything in it and under it
  const treeCount = (f: string) => Object.entries(kindCounts).reduce((n, [k, v]) => (k.toLowerCase() === f.toLowerCase() || k.toLowerCase().startsWith(f.toLowerCase() + '/') ? n + v : n), 0);
  async function createFolder(name: string) {
    // typed inside an open folder, the new one goes in it
    const n = (rawFolder !== 'all' && !name.includes('/') ? `${rawFolder}/${name.trim()}` : name.trim()).replace(/\/+/g, '/');
    if (!n) return;
    try {
      await addRawFolder(creative.id, n);
      toast.success(`Folder "${n}" ready`);
      setUploadCategory(n); setUploadKind('raw'); setActiveKind('raw'); setRawFolder(n); setPicked(n);
      qc.invalidateQueries({ queryKey: ['creatives', creative.id] });
    } catch { toast.error('Could not create the folder'); }
  }
  const visibleIds = visible.map((a) => a.id);
  // Every ticked file is one this person may delete (their own, raws not yet approved).
  const ownDeletable = selected.size > 0 && (assets.data ?? []).filter((a) => selected.has(a.id)).every((a) => a.can_delete);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  const toggleOne = (id: string) => setSelected((cur) => { const n = new Set(cur); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const selectAllVisible = () => setSelected((cur) => {
    const n = new Set(cur);
    visibleIds.forEach((id) => n.add(id));
    return n;
  });
  const everyId = (assets.data ?? []).map((a) => a.id);
  const everythingSelected = everyId.length > 0 && everyId.every((id) => selected.has(id));
  const selectEverything = () => setSelected(new Set(everyId));
  // A folder is selected when every file in it is. Ticking it adds them all,
  // ticking again takes them all out, so folders and files share one selection.
  const folderIds = (f: string) => (assets.data ?? []).filter((a) => { const c = a.kind === fk ? catOf(a) : null; return !!c && (c.toLowerCase() === f.toLowerCase() || c.toLowerCase().startsWith(f.toLowerCase() + '/')); }).map((a) => a.id);
  const folderChecked = (f: string) => { const ids = folderIds(f); return ids.length > 0 && ids.every((id) => selected.has(id)); };
  const toggleFolder = (f: string) => setSelected((cur) => {
    const n = new Set(cur); const ids = folderIds(f);
    if (ids.length && ids.every((id) => n.has(id))) ids.forEach((id) => n.delete(id)); else ids.forEach((id) => n.add(id));
    return n;
  });
  const clearSelection = () => setSelected(new Set());
  const refreshAssets = () => {
    qc.invalidateQueries({ queryKey: ['assets', creative.id] });
    qc.invalidateQueries({ queryKey: ['stages', creative.id] });
    qc.invalidateQueries({ queryKey: ['creatives', creative.id] });
    qc.invalidateQueries({ queryKey: ['creatives'] });
  };
  // ---- Downloads go to the Download Center: one at a time, in order, named
  // "code - title - kind - folder - file". On a desktop Chromium the person picks
  // a folder once and the files stream into it with progress; elsewhere each
  // file is handed to the browser in turn. No zip: nothing to build, no cap.
  const downloads = useDownloadCenter();
  const KIND_ORDER: string[] = ['brief_doc', 'reference', 'raw', 'edit', 'final', 'static', 'poster', 'copy', 'other'];
  const natural = (a: string, b: string) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
  const inDownloadOrder = (ids: string[]) => (assets.data ?? [])
    .filter((a) => ids.includes(a.id) && (a.attachment_url || a.download_url))
    .sort((a, b) => (KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind))
      || natural(a.category ?? '', b.category ?? '') || natural(a.filename, b.filename));
  // Raws download by folder tree (Sarah, 2026-11-04): with no folder open, Download takes
  // every raw (it counted the empty file grid, 0, and did nothing); in a folder it takes the
  // folder and everything under it, the same files its tile counts. A folder with the
  // photos at its top and the videos in a subfolder came down as photos only.
  const rawTreeIds = (folder: string | null) => (assets.data ?? [])
    .filter((a) => a.kind === 'raw' && (folder === null
      || (a.category ?? 'any').toLowerCase() === folder.toLowerCase()
      || (a.category ?? '').toLowerCase().startsWith(folder.toLowerCase() + '/')))
    .map((a) => a.id);
  const dlIds = selected.size ? Array.from(selected)
    : view === 'stages' ? []
    : activeKind === 'raw' && !favOnly ? rawTreeIds(rawFolderOpen ? rawFolder : null)
    : visibleIds;
  async function downloadSelected(ids: string[]) {
    const list = inDownloadOrder(ids);
    if (!list.length) return;
    // The folder picker must be opened from the click itself.
    const dir = downloads.canPickFolder ? await downloads.pickFolder() : null;
    downloads.enqueue(list.map((a) => ({
      assetId: a.id, creativeId: creative.id, creativeCode: creative.code, creativeTitle: creative.title,
      kind: a.kind, category: a.category ?? undefined,
      name: a.download_name || a.filename, size: a.size_bytes ?? 0,
      url: (a.attachment_url || a.download_url) as string,
    })), dir);
    toast.success(`${list.length} file${list.length === 1 ? '' : 's'} queued in the Download Center`);
    setSelected(new Set());
  }
  async function applyKind(k: AssetKind, where?: string) {
    if (!k || selected.size === 0) return;
    setBulkBusy('kind');
    try {
      const r = await bulkAssets(Array.from(selected), 'set_kind', k, where || undefined);
      const place = k === 'edit' && where ? `, ${EDIT_PHASE_LABEL[where] ?? where}` : k === 'raw' && where && where !== 'Any' ? `, folder ${where}` : '';
      toast.success(`${r.affected} file${r.affected === 1 ? '' : 's'} now ${k.replace('_', ' ')}${place}`);
      setSelected(new Set()); setRekind(''); setRekindWhere(''); refreshAssets();
    } catch { toast.error('Could not change the kind'); } finally { setBulkBusy(''); }
  }
  async function applyPhase(p: string) {
    if (!p || selected.size === 0) return;
    setBulkBusy('kind');
    try {
      const r = await bulkAssets(Array.from(selected), 'set_category', undefined, p);
      toast.success(`${r.affected} cut${r.affected === 1 ? '' : 's'} moved to ${EDIT_PHASE_LABEL[p] ?? p}`);
      setSelected(new Set()); refreshAssets();
    } catch { toast.error('Could not move the files'); } finally { setBulkBusy(''); }
  }

  function onPickFiles(files: FileList | File[] | null, kind: AssetKind = uploadKind) {
    if (!files || files.length === 0) return;
    if (refusedHere) { toast.error(refusedHere); return; }
    // every file uploads, camera sidecars (.XML) and system files included (Aziz 2026-09-29)
    const list = Array.from(files);
    if (!list.length) return;
    if (kind === 'raw') {
      setWizardFiles(list);
      return;
    }
    list.forEach((f) => startUpload(f, kind));
    toast.success(`${list.length} file${list.length === 1 ? '' : 's'} queued in the Upload Center`);
  }
  // Rename (or move) the open folder; its files and subfolders follow (server side, one call)
  const [renaming, setRenaming] = useState<string | null>(null);
  async function doRename(oldPath: string, name: string) {
    const leaf = name.trim().split('/').map((x) => x.trim()).filter(Boolean).join('/');
    if (!leaf) return;
    const parent = parentOf(oldPath);
    const next = parent === 'all' ? leaf : `${parent}/${leaf}`;
    try {
      const r = await renameFolder(creative.id, fk, oldPath, next);
      toast.success(`Folder renamed to "${r.folder}" (${r.moved} file${r.moved === 1 ? '' : 's'})`);
      setRawFolder(r.folder); setPicked(r.folder); if (fk === 'raw') setUploadCategory(r.folder);
      refreshAssets();
    } catch (e: unknown) {
      toast.error((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Could not rename the folder');
    }
  }
  // Move ticked references, statics, posters or brief documents into a folder, or back to the top level
  const [newFolderFor, setNewFolderFor] = useState(false);
  async function applyKindFolder(path: string) {
    if (selected.size === 0) return;
    setBulkBusy('kind');
    try {
      const r = await bulkAssets(Array.from(selected), 'set_folder', undefined, path);
      toast.success(`${r.affected} file${r.affected === 1 ? '' : 's'} moved ${path ? `to ${path.split('/').join(' / ')}` : 'to the top level'}`);
      setSelected(new Set()); refreshAssets();
    } catch { toast.error('Could not move the files'); } finally { setBulkBusy(''); }
  }
  const selectedFolderKind = selectedKinds.size === 1 ? Array.from(selectedKinds)[0] : null;
  const moveOptions = useMemo(() => {
    if (!selectedFolderKind || selectedFolderKind === 'raw' || !FOLDER_KINDS.includes(selectedFolderKind)) return [];
    const seen = new Map<string, string>();
    for (const a of assets.data ?? []) if (a.kind === selectedFolderKind && a.category && !seen.has(a.category.toLowerCase())) seen.set(a.category.toLowerCase(), a.category);
    return [{ value: '__top__', label: 'Top level (no folder)' },
      ...Array.from(seen.values()).sort((a, b) => a.localeCompare(b, undefined, { numeric: true })).map((v) => ({ value: v, label: v.split('/').join(' / ') })),
      { value: '__new__', label: '+ New folder...' }];
  }, [selectedFolderKind, assets.data]);
  async function applyFolder(c: RawCategory) {
    if (!c || selected.size === 0) return;
    setBulkBusy('kind');
    try {
      const r = await bulkAssets(Array.from(selected), 'set_category', undefined, c);
      toast.success(`${r.affected} raw${r.affected === 1 ? '' : 's'} moved to ${c}`);
      setSelected(new Set()); setRefolder(''); refreshAssets();
    } catch { toast.error('Could not move the files'); } finally { setBulkBusy(''); }
  }

  // Every upload goes to the Upload Center, raws through the folder wizard and
  // everything else straight here. One queue means one place to watch, one shared
  // pool, and no upload that dies because this card unmounted.
  function startUpload(file: File, kind: AssetKind = uploadKind) {
    const locked = lockedWhy(kind === 'edit' ? uploadPhase : kind === 'final' ? 'final' : null);
    if (locked) { toast.error(locked); return; }
    // a reference, static, poster or brief document keeps the folder it was dropped or
    // picked from, under the folder open on its tab
    const base = folderKind === kind && rawFolder !== 'all' ? rawFolder : '';
    const own = droppedFolder(file);
    const folder = own ? (base ? `${base}/${own}` : own) : base;
    uploads.enqueue([{
      file,
      creativeId: creative.id,
      creativeCode: creative.code,
      creativeTitle: creative.title,
      kind,
      category: kind === 'raw' ? uploadCategory : kind === 'edit' ? uploadPhase : kind === 'final' ? 'final' : (folder || undefined),
    }]);
  }

  // Drag-drop on whole card
  const [isDrag, setIsDrag] = useState(false);

  // Selection bar: pick many, then download, re-kind or delete them in one go. Shown in
  // both views: By stage became the default on 2026-09-29 without it, and the team
  // lost select (Mahmoud 2026-09-30).
  const bulkBar = (view === 'stages' ? everyId.length > 0 || selected.size > 0
    : (visible.length > 0 || selected.size > 0 || (activeKind === 'raw' && !rawFolderOpen && (counts.raw ?? 0) > 0))) && (
        <div className="flex flex-wrap items-center gap-2 mb-3 text-xs" data-testid="asset-bulk-bar">
          {/* Two plain controls rather than one that changes meaning. A single
              toggle read "Clear selection" the moment everything was selected, so
              there was no way to say "select all" without first guessing what the
              button would do. */}
          {view === 'files' && (<>
          <button onClick={() => setFavOnly((v) => !v)} data-testid="fav-filter" aria-pressed={favOnly}
            className={cn('inline-flex items-center gap-1.5 hover:text-muted-strong', favOnly ? 'text-amber-400' : 'text-muted')}>
            <Star size={14} fill={favOnly ? 'currentColor' : 'none'} /> Favorites
          </button>
          <button onClick={selectAllVisible} data-testid="select-all"
            disabled={allVisibleSelected}
            className="inline-flex items-center gap-1.5 text-muted hover:text-muted-strong disabled:opacity-40 disabled:hover:text-muted">
            {allVisibleSelected ? <CheckSquare size={14} className="text-accent-400" /> : <Square size={14} />}
            Select all ({visible.length})
          </button>
          </>)}
          <button onClick={selectEverything} data-testid="select-everything"
            disabled={everythingSelected || everyId.length === 0}
            title="Every file on this creative, all kinds and all folders"
            className="inline-flex items-center gap-1.5 text-muted hover:text-muted-strong disabled:opacity-40 disabled:hover:text-muted">
            {everythingSelected ? <CheckSquare size={14} className="text-accent-400" /> : <Square size={14} />}
            Everything ({everyId.length})
          </button>
          <button onClick={clearSelection} data-testid="clear-all"
            disabled={selected.size === 0}
            className="inline-flex items-center gap-1.5 text-muted hover:text-muted-strong disabled:opacity-40 disabled:hover:text-muted">
            <Square size={14} /> Clear all
          </button>
          {selected.size > 0 && <span className="text-accent-400 font-medium">{selected.size} selected</span>}
          <span className="ml-auto flex flex-wrap items-center gap-2">
            <Button size="sm" variant="secondary" icon={<Download size={12} />} disabled={!!bulkBusy || dlIds.length === 0}
              data-testid="bulk-download"
              title="One at a time, in order, each named with the creative, kind and folder"
              onClick={() => downloadSelected(dlIds)}>
              {selected.size ? `Download ${selected.size} file${selected.size === 1 ? '' : 's'}` : `Download ${dlIds.length} file${dlIds.length === 1 ? '' : 's'}`}
            </Button>
            {isStaff && canWrite && selected.size > 0 && (
              <>
                {/* a file lives in the stage it was uploaded at: only an admin moves it (Aziz 2026-10-30) */}
                {isAdminHere && <Select size="sm" value={rekind} onChange={(v) => { setRekind(v as AssetKind); setRekindWhere(''); }} placeholder="Change kind to..."
                  options={ALL_KINDS.map((k) => ({ value: k, label: k.replace('_', ' ') }))} className="w-40" data-testid="rekind" />}
                {rekind === 'raw' && (
                  <Select size="sm" value={rekindWhere} onChange={setRekindWhere} placeholder="Into folder..." data-testid="rekind-folder"
                    options={folderOptions} className="w-44" searchable />
                )}
                {rekind === 'edit' && (
                  <Select size="sm" value={rekindWhere} onChange={setRekindWhere} placeholder="Which phase..." data-testid="rekind-phase"
                    options={EDIT_PHASES.map((p) => ({ value: p, label: EDIT_PHASE_LABEL[p] }))} className="w-48" />
                )}
                {rekind && (
                  <Button size="sm" loading={bulkBusy === 'kind'} disabled={(rekind === 'raw' || rekind === 'edit') && !rekindWhere}
                    onClick={() => applyKind(rekind as AssetKind, rekindWhere)} data-testid="rekind-apply">
                    Apply
                  </Button>
                )}
                {!rekind && moveOptions.length > 0 && (
                  <Select size="sm" value="" placeholder="Move to folder..." options={moveOptions} className="w-48" searchable data-testid="move-kind-folder"
                    onChange={(v) => { if (v === '__new__') setNewFolderFor(true); else applyKindFolder(v === '__top__' ? '' : v); }} />
                )}
                {!rekind && selectedKinds.size === 1 && selectedKinds.has('raw') && (
                  <Select size="sm" value={refolder} onChange={(v) => { setRefolder(v as RawCategory); applyFolder(v as RawCategory); }} placeholder="Move to folder..."
                    options={folderOptions} className="w-44" searchable data-testid="move-folder" />
                )}
                {isAdminHere && !rekind && selectedKinds.size === 1 && selectedKinds.has('edit') && (
                  <Select size="sm" value="" onChange={applyPhase} placeholder="Move to phase..." data-testid="move-phase"
                    options={EDIT_PHASES.map((p) => ({ value: p, label: EDIT_PHASE_LABEL[p] }))} className="w-48" />
                )}
                <Button size="sm" variant="danger" icon={<Trash2 size={12} />} disabled={!!bulkBusy} onClick={() => setConfirmBulkDelete(true)}>
                  Delete {selected.size}
                </Button>
              </>
            )}
            {isStaff && !canWrite && selected.size > 0 && ownDeletable && (
              <Button size="sm" variant="danger" icon={<Trash2 size={12} />} disabled={!!bulkBusy} onClick={() => setConfirmBulkDelete(true)} data-testid="asset-bulk-delete-own">
                Delete {selected.size}
              </Button>
            )}
          </span>
        </div>
  );

  return (
    <Card>
      <button type="button" onClick={togglePanel} aria-expanded={panelOpen}
        className={cn('w-full flex items-center gap-2 text-left', panelOpen && 'mb-3')}>
        <CollapseChevron open={panelOpen} />
        <ImageIcon size={16} className="text-accent-400" />
        <h3 className="font-medium text-muted-strong text-sm">Assets</h3>
        <span className="text-xs text-muted ml-auto">{counts.all} total</span>
      </button>

      {panelOpen && (<>
      <div className="flex gap-1.5 mb-3" role="tablist" aria-label="How to show the files">
        {(['stages', 'files'] as const).map((v) => (
          <button key={v} role="tab" aria-selected={view === v} onClick={() => showView(v)} data-testid={`assets-view-${v}`}
            className={cn('px-3 py-1.5 rounded-full border text-xs', view === v ? 'border-accent-400 text-accent-400 bg-accent-600/10' : 'border-slate-800 text-muted hover:text-muted-strong')}>
            {v === 'stages' ? 'By stage' : 'All files'}
          </button>
        ))}
      </div>
      {view === 'files' && (<>
      {/* Subtabs */}
      <div className="flex flex-wrap gap-1.5 mb-2">
        <SubTab label="All" count={counts.all} active={activeKind === 'all'} onClick={() => openTab('all')} />
        {ALL_KINDS.map((k) => (
          <SubTab
            key={k}
            label={k.replace('_', ' ')}
            count={counts[k] ?? 0}
            icon={KIND_ICON[k]}
            active={activeKind === k}
            onClick={() => openTab(k)}
          />
        ))}
        {isStaff && canWrite && (
          <button onClick={() => setPickingOther(true)} data-testid="add-from-other-creative"
            className="ml-auto text-[11px] px-2.5 py-1 rounded-full border border-slate-700 text-muted hover:text-accent-400 hover:border-accent-400/50 inline-flex items-center gap-1">
            <Copy size={11} /> From another creative
          </button>
        )}
      </div>
      {/* One folder per edit phase. They share a kind, so without this the rough cuts,
          the colour passes and the inserts all land in one grid together. */}
      {activeKind === 'edit' && (
        <div className="flex flex-wrap gap-1.5 mb-4" data-testid="edit-phases">
          <SubTab label="All" count={counts.edit} active={editPhase === 'all'} onClick={() => setEditPhase('all')} />
          {EDIT_PHASES.map((p) => (
            <SubTab key={p} label={EDIT_PHASE_LABEL[p]} count={editCounts[p]} active={editPhase === p}
              onClick={() => setEditPhase(p)} />
          ))}
        </div>
      )}

      </>)}
      {/* Upload area */}
      {isStaff && (
        <div
          className={cn(
            'border-2 border-dashed rounded-xl p-4 mb-4 transition',
            isDrag ? 'border-accent-400 bg-accent-600/10' : 'border-slate-800 bg-slate-900/30'
          )}
          data-testid="assets-dropzone"
          onDragOver={(e) => { e.preventDefault(); setIsDrag(true); }}
          onDragLeave={() => setIsDrag(false)}
          onDrop={(e) => {
            e.preventDefault();
            setIsDrag(false);
            const dt = e.dataTransfer;
            void filesFromDataTransfer(dt).then((fs) => {
              const n = droppedFolders(fs).length;
              // folders become raw folders, as before, unless a kind with folders was chosen
              // (Upload as, or the References tab): then they keep that kind (2026-10-15)
              const k: AssetKind = strict ? uploadKind : (n && !(kindChosen && FOLDER_KINDS.includes(uploadKind)) ? 'raw' : uploadKind);
              if (n) {
                setUploadKind(k);
                toast.success(`${n} folder${n === 1 ? '' : 's'} dropped: each becomes a ${k === 'raw' ? 'raw' : k.replace('_', ' ')} folder`);
              }
              onPickFiles(fs, k);
            });
          }}
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            {strict ? (
              <div data-testid="upload-goes-to">
                <div className="text-xs text-muted mb-1.5">Uploads go to</div>
                <div className="h-10 flex items-center px-3 rounded-lg border border-slate-800 bg-bg text-sm text-muted-strong">{stageFiling.label}</div>
              </div>
            ) : (
            <Select
              label="Upload as"
              value={uploadKind}
              onChange={(v) => chooseKind(v as AssetKind)}
              options={ALL_KINDS.map((k) => ({ value: k, label: k.replace('_', ' ') }))}
              data-testid="upload-kind"
            />
            )}
            {refusedHere && (
              <div className="md:col-span-2 text-sm text-amber-300" data-testid="upload-refused">{refusedHere}</div>
            )}
            {!strict && uploadKind === 'edit' && (openPhase ? (
              <div data-testid="upload-phase-locked">
                <div className="text-xs text-muted mb-1.5">Phase</div>
                <div className="h-10 flex items-center px-3 rounded-lg border border-slate-800 bg-bg text-sm text-muted-strong">{phaseLabels[openPhase]}<span className="text-[10px] text-muted ml-2">the step open now</span></div>
              </div>
            ) : (
              <Select label="Phase" value={uploadPhase} onChange={(v) => setUploadPhase(v as 'rough' | 'colour' | 'final')}
                options={(['rough', 'colour', 'final'] as const).filter((p) => !lockedWhy(p)).map((p) => ({ value: p, label: phaseLabels[p] }))} />
            ))}
            {uploadLocked && (
              <div className="sm:col-span-2 text-[11px] text-amber-300" data-testid="upload-stage-locked">{uploadLocked}</div>
            )}
            {uploadKind === 'raw' && (
              <Select label="Raw folder" value={uploadCategory} onChange={(v) => { if (v === '__new__') setNewFolder(true); else setUploadCategory(v); }} searchable
                options={[...folderOptions, { value: '__new__', label: '+ New folder...' }]} />
            )}
            <div className={cn('flex flex-wrap items-center gap-2 min-w-0', uploadKind === 'raw' || uploadKind === 'edit' ? '' : 'md:col-span-2')}>
              <Button
                onClick={() => fileRef.current?.click()}
                icon={<Upload size={14} />}
                className="shrink-0"
                disabled={!!refusedHere}
              >
                Pick files
              </Button>
              {FOLDER_KINDS.includes(uploadKind) && (
                <>
                  <Button variant="secondary" className="shrink-0" onClick={() => dirRef.current?.click()} data-testid="pick-folder" disabled={!!refusedHere}>Pick a folder</Button>
                  <input ref={dirRef} type="file" multiple hidden data-testid="assets-folder-input" {...({ webkitdirectory: '' } as Record<string, string>)}
                    onChange={(e) => { const fs = Array.from(e.target.files ?? []); e.currentTarget.value = ''; const n = droppedFolders(fs).length; if (n) toast.success(`${n} folder${n === 1 ? '' : 's'} picked: each becomes a ${uploadKind === 'raw' ? 'raw' : uploadKind.replace('_', ' ')} folder`); onPickFiles(fs); }} />
                </>
              )}
              <span className="text-xs text-muted basis-full sm:basis-auto">or drop files, or whole folders with their subfolders</span>
              <input
                ref={fileRef}
                type="file"
                multiple
                hidden
                data-testid="assets-file-input"
                onChange={(e) => { onPickFiles(Array.from(e.target.files ?? [])); e.currentTarget.value = ''; }}
              />
            </div>
          </div>

          {/* Progress lives in the dock and the Upload Center now, so it stays
              visible after this card unmounts. */}
          {(uploads.active + uploads.queued) > 0 && (
            <div className="mt-3 pt-3 border-t border-slate-800 text-xs text-muted">
              {uploads.active + uploads.queued} file{uploads.active + uploads.queued === 1 ? '' : 's'} uploading.
              {' '}<Link to="/uploads" className="text-accent-400 hover:underline">Upload Center</Link>
            </div>
          )}
        </div>
      )}

      {view === 'stages' ? (<>
        {bulkBar}
        <StageView creativeId={creative.id} assets={stageAssets}
          selected={selected}
          onSelect={(ids, on) => setSelected((cur) => { const n = new Set(cur); ids.forEach((id) => (on ? n.add(id) : n.delete(id))); return n; })}
          renderCard={(a, list) => (
            <AssetCard
              key={a.id}
              phaseLabel={phaseLabels}
              asset={a}
              reviews={reviewsByAsset.get(a.id) ?? []}
              onOpen={() => { setStrip(list); setReviewing(a); }}
              onToggleFavorite={async () => {
                const next = !a.is_favorite;
                await patchAsset(a.id, { is_favorite: next });
                toast.success(next ? 'Marked as a favorite' : 'Favorite removed');
                qc.invalidateQueries({ queryKey: ['assets', creative.id] });
              }}
              onDelete={() => setPendingDelete(a)}
              canEdit={a.active !== false && isStaff && (canWrite || a.uploaded_by === me?.id)}
              canDelete={a.active !== false && (a.can_delete ?? (isStaff && (canWrite || a.uploaded_by === me?.id)))}
              selected={selected.has(a.id)}
              onToggle={() => toggleOne(a.id)}
              inRound
            />
          )} />
      </>) : (<>
      {/* Raw folders as folders. Explorer rules, because that is what everyone
          already knows: one click picks a folder, two opens it. Opening filters the
          grid below to that folder and the crumb walks back out. */}
      {folderKind && rawFolder === 'all' && (folderKind === 'raw' || kindFolders.length > 0) && (
        <div className="mb-3" data-testid={folderKind === 'raw' ? 'raw-folders' : 'kind-folders'} data-kind={folderKind}>
          <div className="flex items-center gap-1.5 mb-2">
            <FolderOpen size={13} className="text-muted" />
            <span className="text-[11px] text-muted">Folders{folderKind !== 'raw' ? ` · ${childrenOf('all').length}` : ''}</span>
            {folderKind === 'raw' && isStaff && canWrite && !refusedHere && (
              <button onClick={() => setPickingMaterial(true)} data-testid="add-from-material"
                className="ml-auto text-[11px] px-2.5 py-1 rounded-full border border-slate-700 text-muted hover:text-accent-400 hover:border-accent-400/50 inline-flex items-center gap-1">
                <Clapperboard size={11} /> Add from raw material
              </button>
            )}
            {folderKind === 'raw' && isStaff && (
              <button onClick={() => setNewFolder(true)} data-testid="new-folder"
                className="text-[11px] px-2.5 py-1 rounded-full border border-dashed border-slate-700 text-muted hover:text-accent-400 hover:border-accent-400/50 inline-flex items-center gap-1">
                <FolderPlus size={11} /> New folder
              </button>
            )}
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-1">
            {folderKind === 'raw' && (
            <FolderTile
              label="All raws" count={counts.raw ?? 0} all
              picked={picked === '__all__'}
              onPick={() => setPicked('__all__')}
              onOpen={() => { setPicked('__all__'); setRawFolder('all'); }}
            />
            )}
            {childrenOf('all').map((f) => (
              <FolderTile
                key={f} label={leafOf(f)} count={treeCount(f)}
                picked={picked === f}
                coarse={coarse}
                checked={folderChecked(f)}
                onCheck={treeCount(f) > 0 ? () => toggleFolder(f) : undefined}
                onPick={() => setPicked(f)}
                onOpen={() => { setPicked(f); setRawFolder(f); if (folderKind === 'raw') setUploadCategory(f); }}
              />
            ))}
          </div>
          {folderKind !== 'raw' && visible.length > 0 && <div className="text-[11px] text-muted mt-3" data-testid="loose-files-note">Not in a folder</div>}
        </div>
      )}

      {/* Inside a folder: the way back, and what is in here. */}
      {folderKind && rawFolder !== 'all' && (
        <div className="mb-3" data-testid={folderKind === 'raw' ? 'raw-folders' : 'kind-folders'} data-kind={folderKind} data-open={rawFolder}>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => { const up = parentOf(rawFolder); setRawFolder(up); setPicked(up === 'all' ? '__all__' : up); if (folderKind === 'raw') setUploadCategory(up === 'all' ? 'Any' : up); }} data-testid="folder-back"
              className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-lg border border-slate-800 text-muted hover:text-accent-400 hover:border-accent-400/50">
              <ChevronLeft size={12} /> <span className="max-w-[45vw] truncate" dir="auto">{parentOf(rawFolder) === 'all' ? 'All folders' : leafOf(parentOf(rawFolder))}</span>
            </button>
            <FolderOpen size={13} className="text-accent-400" />
            {/* a long Arabic path wraps inside the card instead of widening the page on a phone */}
            <span className="min-w-0 max-w-full text-xs text-muted-strong [overflow-wrap:anywhere]" data-testid="folder-crumb"><FolderPath path={rawFolder} /></span>
            <span className="text-[11px] text-muted">{kindCounts[rawFolder] ?? 0} file{(kindCounts[rawFolder] ?? 0) === 1 ? '' : 's'}{childrenOf(rawFolder).length ? ` · ${childrenOf(rawFolder).length} subfolder${childrenOf(rawFolder).length === 1 ? '' : 's'}` : ''}</span>
            <span className="ml-auto flex items-center gap-1.5">
            {isStaff && canWrite && !(folderKind === 'raw' && rawFolder.toLowerCase() === 'any') && (
              <button onClick={() => setRenaming(rawFolder)} data-testid="rename-folder"
                className="text-[11px] px-2.5 py-1 rounded-full border border-slate-700 text-muted hover:text-accent-400 hover:border-accent-400/50 inline-flex items-center gap-1">
                <PencilLine size={11} /> Rename
              </button>
            )}
            {folderKind === 'raw' && isStaff && (
              <button onClick={() => setNewFolder(true)} data-testid="new-folder"
                className="text-[11px] px-2.5 py-1 rounded-full border border-dashed border-slate-700 text-muted hover:text-accent-400 hover:border-accent-400/50 inline-flex items-center gap-1">
                <FolderPlus size={11} /> New subfolder
              </button>
            )}
            </span>
          </div>
          {childrenOf(rawFolder).length > 0 && (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-1 mt-2" data-testid="subfolders">
              {childrenOf(rawFolder).map((f) => (
                <FolderTile
                  key={f} label={leafOf(f)} count={treeCount(f)}
                  picked={picked === f}
                  coarse={coarse}
                  checked={folderChecked(f)}
                  onCheck={treeCount(f) > 0 ? () => toggleFolder(f) : undefined}
                  onPick={() => setPicked(f)}
                  onOpen={() => { setPicked(f); setRawFolder(f); if (folderKind === 'raw') setUploadCategory(f); }}
                />
              ))}
            </div>
          )}
        </div>
      )}
      <PromptDialog
        open={!!renaming}
        title={renaming ? `Rename folder ${renaming.split('/').join(' / ')}` : 'Rename folder'}
        message="Every file in it, and every folder inside it, moves with it. A name that already exists merges the two."
        placeholder="New name"
        defaultValue={renaming ? leafOf(renaming) : ''}
        confirmLabel="Rename"
        onConfirm={(v) => { const from = renaming; setRenaming(null); if (from) void doRename(from, v); }}
        onCancel={() => setRenaming(null)}
      />
      <PromptDialog
        open={newFolderFor}
        title="Move to a new folder"
        message="Name the folder. A slash nests: Ameson/Before and after."
        placeholder="Folder name"
        confirmLabel="Move"
        onConfirm={(v) => { setNewFolderFor(false); const n = v.split('/').map((x) => x.trim()).filter(Boolean).join('/'); if (n) void applyKindFolder(n); }}
        onCancel={() => setNewFolderFor(false)}
      />
      <PromptDialog
        open={newFolder}
        title={rawFolder === 'all' ? 'New raw folder' : `New subfolder in ${rawFolder.split('/').join(' / ')}`}
        message={rawFolder === 'all'
          ? 'Name it the way the team talks about it, for example Dr Diana chair 2, or one of the standard ones: Talking, Treatments, Mapping, Before, After, Reviews. A slash nests: Day 1/Dr Sara.'
          : 'It goes inside the open folder. A slash nests deeper: Chair 2/Close-ups.'}
        placeholder="Folder name"
        confirmLabel="Create folder"
        onConfirm={(v) => { setNewFolder(false); createFolder(v); }}
        onCancel={() => setNewFolder(false)}
      />

      {/* Selection bar: pick many, then download, re-kind or delete them in one go.
          Also on the folder view, where whole folders are ticked. */}
      {bulkBar}

      {/* Grid */}
      {assets.isLoading && <div className="text-xs text-muted py-4 text-center">Loading assets...</div>}
      {!assets.isLoading && activeKind === 'raw' && !rawFolderOpen && (
        <div className="text-xs text-muted py-8 text-center">
          {folders.length === 0 ? 'No raw folders yet.' : ''}
        </div>
      )}
      {!assets.isLoading && activeKind === 'all' && (counts.raw ?? 0) > 0 && (
        <div className="text-[11px] text-muted mb-3 inline-flex items-center gap-1.5">
          <FolderOpen size={12} />
          {counts.raw} raw
          <button onClick={() => setActiveKind('raw')} className="text-accent-400 hover:underline">Open Raw</button>
        </div>
      )}
      {!assets.isLoading && visible.length === 0 && !(activeKind === 'raw' && !rawFolderOpen)
        && !(folderKind && folderKind !== 'raw' && (rawFolder !== 'all' ? childrenOf(rawFolder).length > 0 : kindFolders.length > 0)) && (
        <div className="text-xs text-muted py-8 text-center">
          {activeKind === 'all' ? 'No assets yet.' : `No ${activeKind.replace('_', ' ')} yet.`}
          {isStaff && <div className="mt-1 text-[10px]">Drag a file above to add one.</div>}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {visible.map((a) => (
          <AssetCard
            key={a.id}
            phaseLabel={phaseLabels}
            asset={a}
            reviews={reviewsByAsset.get(a.id) ?? []}
            onOpen={() => { setStrip(null); setReviewing(a); }}
            onToggleFavorite={async () => {
              const next = !a.is_favorite;
              await patchAsset(a.id, { is_favorite: next });
              toast.success(next ? 'Marked as a favorite' : 'Favorite removed');
              qc.invalidateQueries({ queryKey: ['assets', creative.id] });
            }}
            onDelete={() => setPendingDelete(a)}
            canEdit={isStaff && (canWrite || a.uploaded_by === me?.id)}
            canDelete={a.can_delete ?? (isStaff && (canWrite || a.uploaded_by === me?.id))}
            selected={selected.has(a.id)}
            onToggle={() => toggleOne(a.id)}
            onNote={() => setNoteFor(a)}
          />
        ))}
      </div>
      </>)}

      {pickingOther && <OtherCreativePicker creativeId={creative.id} onClose={() => setPickingOther(false)} />}
      {pickingMaterial && (
        <MaterialPicker creativeId={creative.id} clientId={creative.client_id} onClose={() => setPickingMaterial(false)} onLinked={() => setPickingMaterial(false)} />
      )}
      {wizardFiles && (
        <BulkUploadDialog
          creatives={[{ id: creative.id, code: creative.code, title: creative.title }]}
          defaultKind="raw" fixedCreativeId={creative.id} initialFiles={wizardFiles} folders={folders} defaultFolder={uploadCategory}
          onClose={() => setWizardFiles(null)}
          onDone={() => { setWizardFiles(null); refreshAssets(); }}
        />
      )}
      {noteFor && (
        <QuickNote asset={noteFor} onClose={() => setNoteFor(null)} onPosted={() => { setNoteFor(null); qc.invalidateQueries({ queryKey: ['creative-reviews', creative.id] }); }} />
      )}

      <ConfirmDialog
        open={confirmBulkDelete}
        title={`Delete ${selected.size} asset${selected.size === 1 ? '' : 's'}?`}
        message={<>They will be soft-deleted and can be recovered for 30 days.</>}
        confirmLabel={`Delete ${selected.size}`}
        cancelLabel="Keep"
        tone="danger"
        onCancel={() => setConfirmBulkDelete(false)}
        onConfirm={async () => {
          setBulkBusy('delete');
          try {
            const r = await bulkAssets(Array.from(selected), 'delete');
            toast.success(`${r.affected} asset${r.affected === 1 ? '' : 's'} deleted`);
            if (r.skipped?.length) toast.message(`${r.skipped.length} kept: not yours, or raws the art director already approved`);
            setSelected(new Set()); refreshAssets();
          } catch { toast.error('Bulk delete failed'); }
          finally { setBulkBusy(''); setConfirmBulkDelete(false); }
        }}
      />

      {reviewing && (
        <ReviewModal key={reviewing.id} asset={(assets.data ?? []).find((a) => a.id === reviewing.id) ?? reviewing} onClose={() => { setReviewing(null); setStrip(null); setFocus(null); }}
          focusReviewId={focus?.review} startAt={focus?.t}
          siblings={view === 'stages' && strip ? strip : visible} onNavigate={setReviewing}
          onDeleted={(id) => {
            // gone from the strip and the grid at once; the refetch confirms it
            setStrip((s) => (s ? s.filter((x) => x.id !== id) : s));
            qc.setQueryData<MediaAsset[]>(['assets', creative.id], (cur) => cur?.filter((x) => x.id !== id));
            setSelected((cur) => { if (!cur.has(id)) return cur; const n = new Set(cur); n.delete(id); return n; });
            qc.invalidateQueries({ queryKey: ['creatives', creative.id] });
          }} />
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        title="Delete asset?"
        message={pendingDelete && (
          <>
            <FileName name={pendingDelete.filename} className="font-mono text-muted-strong" /> will be soft-deleted.
            It can be recovered for 30 days.
          </>
        )}
        confirmLabel="Delete"
        cancelLabel="Keep"
        tone="danger"
        onCancel={() => setPendingDelete(null)}
        onConfirm={async () => {
          if (!pendingDelete) return;
          try {
            await deleteAsset(pendingDelete.id);
            toast.success('Asset deleted');
            qc.invalidateQueries({ queryKey: ['assets', creative.id] });
            qc.invalidateQueries({ queryKey: ['creatives', creative.id] });
            qc.invalidateQueries({ queryKey: ['creatives'] });
            setPendingDelete(null);
          } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : 'Delete failed';
            toast.error(msg);
          }
        }}
      />
      </>)}
    </Card>
  );
}

// ---------- atoms ----------

function SubTab({ label, count, icon, active, onClick }: { label: string; count: number; icon?: React.ReactNode; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition capitalize',
        active
          ? 'border-accent-400 bg-accent-600/20 text-accent-400'
          : 'border-slate-800 text-muted hover:text-muted-strong'
      )}
    >
      {icon}
      {label} <span className="opacity-60">{count}</span>
    </button>
  );
}

function AssetCard({ asset, reviews, onOpen, onToggleFavorite, onDelete, canEdit, canDelete, selected, onToggle, onNote, inRound = false, phaseLabel = PHASE_NAMES.video }: {
  asset: MediaAsset; reviews: AssetReview[];
  /** the phase names for this piece's kind of work (FE-14) */
  phaseLabel?: Record<string, string>;
  /** shown inside a stage round: the round says which version it is, so no "latest" star */
  inRound?: boolean;
  onOpen: () => void; onToggleFavorite: () => void; onDelete: () => void; canEdit: boolean; canDelete: boolean;
  selected?: boolean; onToggle?: () => void; onNote?: () => void;
}) {
  const isVideo = asset.content_type?.startsWith('video/');
  const [downloading, setDownloading] = useState(false);
  // A phone has no hover: the actions must simply be there, and they must fit.
  const coarse = useCoarsePointer();
  const proxyPending = !!isVideo && (asset.proxy_status === 'queued' || asset.proxy_status === 'pending');
  const proxyPct = Math.min(97, Math.round((asset.proxy_progress ?? 0) * 100));

  // Reviews summary - count THREADS (top-level only), not raw rows. A "thread" is one
  // top-level comment + its replies; one thread is one piece of feedback regardless
  // of how many times people replied.
  const threads = reviews.filter(r => !r.parent_id);
  const openThreads = threads.filter(r => r.status === 'open');
  const hasReviews = threads.length > 0;
  const hasOpenReviews = openThreads.length > 0;
  const recentReviews = useMemo(
    () => [...threads].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()).slice(0, 3),
    [threads],
  );

  async function handleDownload(e: React.MouseEvent) {
    e.stopPropagation();
    if (!asset.download_url || downloading) return;
    setDownloading(true);
    try {
      await downloadAssetFile(asset.download_url, asset.filename);
    } catch (err) {
      toast.error('Download failed');
    } finally {
      setDownloading(false);
    }
  }

  return (
    <article
      data-testid="asset-card"
      className={cn(
        'group relative rounded-lg overflow-hidden border bg-slate-900/50 flex flex-col transition',
        // Open-review accent: amber ring draws attention to assets needing action
        hasOpenReviews ? 'border-amber-400/70 ring-2 ring-amber-400/30 hover:border-amber-300'
                       : 'border-slate-800 hover:border-accent-400/50',
      )}
    >
      {/* The picture, with everything that sits ON it. The name, size and actions sit
          under it, so nothing can overlap however small the card gets (2026-09-29). */}
      <div className="relative aspect-[4/3] w-full shrink-0">
      {/* Preview - server thumbnail first, then direct render of image/video, finally icon fallback */}
      <button data-testid="asset-open" onClick={onOpen}
        className="absolute inset-0 w-full h-full flex items-center justify-center">
        <AssetPreview asset={asset} />

        {isVideo && (
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-accent-500/90 flex items-center justify-center text-white shadow-lg pointer-events-none">
            <Play size={13} className="ml-0.5" />
          </div>
        )}
      </button>

      {/* Selection tick.
          Top-left, above everything, and always visible. It used to sit bottom-right
          at the same z-index as the hover action column, which stacks five buttons
          down the right edge and therefore covered it: the tick could not be pressed
          at all. It was also opacity-0 until hover, and a phone has no hover, so on
          a touch screen it never appeared and nothing could be selected by hand. */}
      {onToggle && (
        <button
          onClick={(e) => { e.stopPropagation(); onToggle(); }}
          aria-label={selected ? 'Unselect' : 'Select'}
          data-testid="asset-select"
          className={cn('absolute top-1.5 left-1.5 z-30 rounded-md p-1.5 ring-1 backdrop-blur transition',
            selected
              ? 'bg-accent-500 text-white ring-white/40'
              : 'bg-slate-950/70 text-white/80 ring-white/25 hover:bg-slate-900')}
        >
          {selected ? <CheckSquare size={14} /> : <Square size={14} />}
        </button>
      )}

      {/* Top badges - solid backdrops so they read against any preview color */}
      <div className={cn('absolute top-1.5 right-1.5 z-10 flex items-start justify-between gap-1 pointer-events-none min-w-0',
        onToggle ? (coarse ? 'left-[46px]' : 'left-10') : 'left-1.5')}>
        <span className={cn('text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded backdrop-blur truncate whitespace-nowrap min-w-0', KIND_COLOR[asset.kind])}>
          {asset.kind.replace('_', ' ')}{asset.kind !== 'edit' && asset.kind !== 'final' && asset.category && asset.category !== 'Any' ? <> · <FolderPath path={asset.category} /></> : ''}{asset.kind === 'edit' && asset.category ? ` · ${phaseLabel[asset.category] ?? asset.category}` : ''}
        </span>
        {asset.is_latest_of_kind && !inRound && (
          <span className="shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded bg-accent-500 text-white ring-1 ring-white/30 shadow-md inline-flex items-center gap-1">
            <Star size={10} fill="currentColor" /> latest
          </span>
        )}
        {asset.active === false && (
          <span className="shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded bg-slate-700 text-white ring-1 ring-white/30 shadow-md"
                title="This version was deleted after it was sent for review. It stays in its round so the history is complete." data-testid="deleted-badge">
            deleted
          </span>
        )}
        {asset.client_approved && (
          <span className="shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded bg-emerald-600 text-white ring-1 ring-white/30 shadow-md"
                title="The clinic approved this file. It is locked: only the owner or a manager can remove it." data-testid="clinic-approved-badge">
            clinic approved
          </span>
        )}
        {asset.is_favorite && (
          <span className="shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded bg-amber-500 text-white ring-1 ring-white/30 shadow-md inline-flex items-center gap-1"
                data-testid="fav-badge">
            <Star size={10} fill="currentColor" /> favorite
          </span>
        )}
      </div>

      {/* Reviews badge: only renders when there ARE reviews. Amber=open, emerald=all resolved.
          Hover surfaces the latest 3 comment previews so long bodies stay tucked away. */}
      {hasReviews && (
        <div className="absolute left-1.5 bottom-1.5 z-20 reviews-badge group/badge">
          <button
            onClick={(e) => { e.stopPropagation(); onOpen(); }}
            className={cn(
              'flex items-center gap-1 px-2 py-1 rounded-full ring-1 backdrop-blur shadow-lg text-[10px] font-bold transition hover:scale-105',
              hasOpenReviews ? 'bg-amber-500 text-white ring-amber-200' : 'bg-emerald-600 text-white ring-emerald-300/40',
            )}
            title={hasOpenReviews ? `${openThreads.length} open · ${threads.length} total` : `${threads.length} resolved`}
          >
            {hasOpenReviews ? <MessageSquare size={11} /> : <Check size={11} />}
            <span>{threads.length}</span>
            {hasOpenReviews && openThreads.length !== threads.length && (
              <span className="opacity-80 font-normal">· {openThreads.length} open</span>
            )}
          </button>

          {/* Hover popover */}
          <div className="pointer-events-none absolute bottom-full left-0 mb-1 w-72 rounded-lg bg-slate-900 ring-1 ring-slate-700 text-left text-[11px] text-white opacity-0 group-hover/badge:opacity-100 transition shadow-2xl z-30 overflow-hidden">
            <div className="px-2.5 py-1.5 border-b border-slate-800 text-[10px] uppercase tracking-wider text-muted bg-slate-950/40">
              {threads.length} review{threads.length === 1 ? '' : 's'}
              {hasOpenReviews && <span className="text-amber-400 ml-1.5">· {openThreads.length} open</span>}
              {!hasOpenReviews && <span className="text-emerald-400 ml-1.5">· all resolved</span>}
            </div>
            {recentReviews.map((r) => {
              const hasDrawing = (r.drawing?.length ?? 0) > 0;
              return (
                <div key={r.id} className="px-2.5 py-1.5 border-b border-slate-800 last:border-0">
                  <div className="font-medium text-accent-300 truncate">
                    {r.author_name}
                    {r.timestamp_sec != null && (
                      <span className="text-muted ml-1.5 font-mono">@ {fmtTime(r.timestamp_sec)}</span>
                    )}
                    {hasDrawing && <span className="text-amber-300 ml-1.5">· {r.drawing!.length} mark</span>}
                    {r.status === 'resolved' && <span className="text-emerald-400 ml-1.5">✓</span>}
                  </div>
                  <div className="text-white/80 line-clamp-3 mt-0.5 whitespace-normal">{r.body}</div>
                </div>
              );
            })}
            {threads.length > recentReviews.length && (
              <div className="px-2.5 py-1 text-center text-muted text-[10px] bg-slate-950/40">
                +{threads.length - recentReviews.length} more · click to open
              </div>
            )}
          </div>
        </div>
      )}

      </div>

      {/* Bottom info bar. The actions live in here as a row, so they can never
          run past the frame: a column of five down the right edge overflowed a
          phone card top and bottom and sat over the badges. On a mouse the row
          shows on hover; on touch it is simply there. */}
      <div className="relative p-2 bg-slate-950/70 border-t border-slate-800/80 pointer-events-none"
           data-testid="asset-bar">
        {proxyPending && (
          <div className="mb-1" data-testid="proxy-card-hint">
            <div className="text-[9px] text-accent-300 flex items-center gap-1 truncate">
              <Loader2 size={9} className="animate-spin shrink-0" />
              <span className="truncate">streaming copy in about {formatEta(asset.proxy_eta_seconds ?? 120)}</span>
            </div>
            <div className="mt-0.5 h-0.5 rounded-full bg-white/15 overflow-hidden">
              <div className="h-full bg-accent-400" style={{ width: `${proxyPct}%` }} />
            </div>
          </div>
        )}
        {asset.version_label ? <Iso className="block text-[10px] text-white/90 truncate">{asset.version_label}</Iso>
          : <FileName name={asset.filename} className="block text-[10px] text-white/90 truncate" />}
        {/\.xml$/i.test(asset.filename) && <div className="text-[9px] text-amber-300/90" data-testid="sidecar-note">camera data, not a clip</div>}
        <div className="text-[9px] text-white/50 flex items-center gap-2">
          {asset.duration_sec != null && <span>{Math.round(asset.duration_sec)}s</span>}
          {asset.size_bytes != null && <span>{humanSize(asset.size_bytes)}</span>}
          <span className="ml-auto">{relTime(asset.uploaded_at)}</span>
        </div>
        {/* always shown: with the details under the picture a hidden row only left a blank strip */}
        <div className="mt-1.5 grid grid-flow-col auto-cols-fr items-center gap-1 pointer-events-auto"
          data-testid="asset-actions">
          {onNote && (
            <button onClick={(e) => { e.stopPropagation(); onNote(); }} title="Add a note (text or voice) without opening the file" data-testid="asset-note" data-compact="true"
              className="w-full h-7 min-w-0 rounded-md bg-slate-950/80 text-white/80 ring-1 ring-white/20 hover:bg-accent-600 hover:text-white flex items-center justify-center">
              <StickyNote size={11} />
            </button>
          )}
          {asset.download_url && !coarse && (
            <a href={asset.download_url} target="_blank" rel="noreferrer"
              className="w-full h-7 min-w-0 rounded-md bg-slate-950/80 text-white/80 ring-1 ring-white/20 hover:bg-accent-600 hover:text-white flex items-center justify-center"
              title="Open in new tab" data-compact="true" onClick={(e) => e.stopPropagation()}>
              <ExternalLink size={11} />
            </a>
          )}
          {(asset.attachment_url || asset.download_url) && (
            asset.attachment_url ? (
              // Google serves this one as an attachment: the browser streams it
              // to disk. Fetching into memory failed on anything over ~1 GB.
              <a href={asset.attachment_url} rel="noreferrer" onClick={(e) => e.stopPropagation()}
                className="w-full h-7 min-w-0 rounded-md bg-slate-950/80 text-white/80 ring-1 ring-white/20 hover:bg-accent-600 hover:text-white flex items-center justify-center"
                title="Download file" data-testid="asset-download" data-compact="true">
                <Download size={11} />
              </a>
            ) : (
              <button onClick={handleDownload} disabled={downloading}
                className="w-full h-7 min-w-0 rounded-md bg-slate-950/80 text-white/80 ring-1 ring-white/20 hover:bg-accent-600 hover:text-white flex items-center justify-center disabled:opacity-50"
                title="Download file" data-testid="asset-download" data-compact="true">
                <Download size={11} />
              </button>
            )
          )}
          {canEdit && (
            <button onClick={(e) => { e.stopPropagation(); onToggleFavorite(); }}
              className={cn('w-full h-7 min-w-0 rounded-md bg-slate-950/80 ring-1 ring-white/20 hover:bg-accent-600 hover:text-white flex items-center justify-center',
                asset.is_favorite ? 'text-amber-400' : 'text-white/80')}
              title={asset.is_favorite ? 'Remove from favorites' : 'Mark as a favorite (best shot)'}
              aria-pressed={!!asset.is_favorite} data-testid="asset-star" data-compact="true">
              <Star size={11} fill={asset.is_favorite ? 'currentColor' : 'none'} />
            </button>
          )}
          {canDelete && (
            <button onClick={(e) => { e.stopPropagation(); onDelete(); }}
              className="w-full h-7 min-w-0 rounded-md bg-slate-950/80 text-white/80 ring-1 ring-white/20 hover:bg-red-600 hover:text-white flex items-center justify-center"
              title="Delete (soft)" data-compact="true">
              <Trash2 size={11} />
            </button>
          )}
        </div>
      </div>

    </article>
  );
}

// Picks the best preview the browser can render in-card without extra plumbing:
//   1. Server-extracted thumbnail (if uploader supplied one)
//   2. The file itself (image src / video first-frame)
//   3. PDF first-page via inline iframe (works for same-origin / signed GCS GETs)
//   4. Kind-themed icon as last resort
/** Is this a touch screen? A phone has no hover and no sensible double tap, so a
 *  folder there opens on the first tap. A mouse keeps explorer rules: click picks,
 *  double click opens. Read live, because a Surface can gain and lose its keyboard. */
function useCoarsePointer(): boolean {
  const [coarse, setCoarse] = useState(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(pointer: coarse)').matches
      : false);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(pointer: coarse)');
    const on = (e: MediaQueryListEvent) => setCoarse(e.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return coarse;
}

/** One folder, drawn as a folder. On a mouse: click picks, double click opens.
 *  On touch: one tap opens. Enter and Space open either way. */
function FolderTile({ label, count, picked, all, coarse, checked, onCheck, onPick, onOpen }: {
  label: string; count: number; picked: boolean; all?: boolean; coarse?: boolean;
  checked?: boolean; onCheck?: () => void;
  onPick: () => void; onOpen: () => void;
}) {
  return (
    <div className="relative">
    {onCheck && (
      <button type="button" onClick={(e) => { e.stopPropagation(); onCheck(); }} data-testid="folder-select" data-compact="true"
        aria-label={checked ? `Unselect folder ${label}` : `Select folder ${label}`} aria-pressed={!!checked}
        // Just left of the folder icon itself, so it reads as THIS folder's tick
        // and not the neighbour's: the cell is wider than the icon.
        style={{ left: 'calc(50% - 36px)', top: 6 }}
        className={cn('absolute z-10 rounded p-0.5 ring-1',
          checked ? 'bg-accent-500 text-white ring-white/40' : 'bg-slate-950/70 text-white/70 ring-white/20 hover:bg-slate-900')}>
        {checked ? <CheckSquare size={12} /> : <Square size={12} />}
      </button>
    )}
    <button
      type="button"
      data-testid="folder-tile"
      title={`${label} - ${count} file${count === 1 ? '' : 's'} (${coarse ? 'tap' : 'double click'} to open)`}
      onClick={coarse ? onOpen : onPick}
      onDoubleClick={coarse ? undefined : onOpen}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); } }}
      className={cn(
        'group w-full flex flex-col items-center gap-1 px-1.5 py-2 rounded-lg border transition select-none',
        picked ? 'border-accent-400 bg-accent-600/15' : 'border-transparent hover:border-slate-800 hover:bg-bg-card/60',
      )}
    >
      <span className="relative">
        {all
          ? <FolderOpen size={30} className={cn('transition', picked ? 'text-accent-400' : 'text-muted group-hover:text-muted-strong')} />
          : <Folder size={30} className={cn('transition', picked ? 'text-accent-400' : 'text-muted group-hover:text-muted-strong')} />}
        {count > 0 && (
          <span title={`${count.toLocaleString()} file${count === 1 ? '' : 's'}`} data-testid="folder-count" data-count={count}
            className="absolute -top-1 -right-1.5 text-[9px] font-semibold px-1 py-px rounded-full bg-slate-800 text-muted-strong min-w-[15px] text-center">
            {count > 99 ? '99+' : count}
          </span>
        )}
      </span>
      <span className={cn('text-[10px] leading-tight text-center line-clamp-2 break-words',
        picked ? 'text-accent-400' : 'text-muted')}>
        <Iso>{label}</Iso>
      </span>
    </button>
    </div>
  );
}

function AssetPreview({ asset }: { asset: MediaAsset }) {
  const shown = showAs(asset);
  const isVideo = shown === 'video';
  const isImage = shown === 'image';
  const isPdf   = shown === 'pdf';

  if (asset.thumbnail_url) {
    return <img src={asset.thumbnail_url} alt={asset.filename} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />;
  }
  if (isImage && asset.download_url) {
    return <img src={asset.download_url} alt={asset.filename} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />;
  }
  const poster = asset.proxy_url || asset.download_url;
  if (isVideo && poster) {
    // #t=0.1 hint nudges the browser to render the first frame as the poster.
    // The proxy when there is one: a poster from a 244 Mbps master costs a
    // multi-megabyte read of the index alone, times every card on the page.
    return (
      <video
        src={`${poster}#t=0.1`}
        preload="metadata"
        muted
        playsInline
        className="absolute inset-0 w-full h-full object-cover bg-black"
      />
    );
  }
  if (isPdf && asset.download_url) {
    return (
      <iframe
        src={`${asset.download_url}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
        title={asset.filename}
        className="absolute inset-0 w-full h-full pointer-events-none bg-white"
      />
    );
  }
  // a RAW or HEIC photo whose preview is on its way, and every other kind of file:
  // an icon with its type, so an XML, a ZIP and an ARW never look alike
  return (
    <div className="flex flex-col items-center gap-1.5" data-testid="asset-type-icon">
      <div className="text-muted/50">
        {shown === 'preview-image' || shown === 'image' ? <ImageIcon size={26} strokeWidth={1.5} />
          : shown === 'text' || shown === 'pdf' || shown === 'document' ? <FileText size={26} strokeWidth={1.5} />
          : shown === 'video' ? <Video size={26} strokeWidth={1.5} /> : <FileIcon size={26} strokeWidth={1.5} />}
      </div>
      <span className="text-[10px] font-semibold tracking-wider px-1.5 py-0.5 rounded bg-slate-800 text-white/85">
        {isJunkFile(asset.filename) ? 'Mac system file' : typeBadge(asset)}{shown === 'preview-image' ? ' · preview soon' : ''}
      </span>
    </div>
  );
}

// Cross-origin GCS signed URLs ignore <a download>, so fetch-as-blob + click a hidden link.
async function downloadAssetFile(url: string, filename: string) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const blob = await res.blob();
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename || 'download';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
}

// ---------- helpers ----------

/** Where a non-admin's upload goes at this stage (upload_rules.filing_for on the server). */
function filingFor(c: Creative, nextEdit: 'rough' | 'colour' | 'final', labels: Record<string, string>):
  { kind: AssetKind | null; label: string; refused: string | null } {
  const st = c.current_stage;
  if (!st || st === 'brief') return { kind: 'reference', label: 'References (the brief)', refused: null };
  if (st === 'shoot' || st === 'raws_review') return { kind: 'raw', label: 'Raws', refused: null };
  if (st === 'edit') {
    const fam = familyOf(c.creative_type);
    if (fam === 'static') return { kind: 'static', label: `${labels.final}: the next round`, refused: null };
    if (fam === 'photo') return { kind: 'final', label: `${labels.final}: the next round`, refused: null };
    return nextEdit === 'final' ? { kind: 'final', label: labels.final, refused: null } : { kind: 'edit', label: labels[nextEdit], refused: null };
  }
  if (st === 'dual_review' || (st === 'done' && !c.client_approved_at))
    return { kind: null, label: 'Nothing while in review', refused: 'This piece is in review. Changes come back as a new round.' };
  return { kind: 'thumbnail', label: 'The thumbnail (by the person posting it)', refused: null };
}

function suggestedKindFor(creativeType: string): AssetKind {
  if (creativeType === 'video' || creativeType === 'reel') return 'raw';
  if (creativeType === 'photo') return 'raw';
  if (creativeType === 'static' || creativeType === 'carousel' || creativeType === 'story') return 'static';
  return 'raw';
}

function fmtTime(sec: number): string {
  if (!isFinite(sec) || sec < 0) sec = 0;
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function humanSize(bytes: number): string {
  if (!bytes) return '';
  const u = ['B', 'KB', 'MB', 'GB'];
  let i = 0; let n = bytes;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(n < 10 ? 1 : 0)} ${u[i]}`;
}

function relTime(iso: string): string {
  const sec = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (sec < 60)    return `${sec}s ago`;
  if (sec < 3600)  return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
}


/** A note on a file from the grid: text, a voice note, or both. Lands in the
 *  file's review thread with no frame pinned. */
function QuickNote({ asset, onClose, onPosted }: { asset: MediaAsset; onClose: () => void; onPosted: () => void }) {
  const [text, setText] = useState('');
  const [mentions, setMentions] = useState<string[]>([]);
  const [voice, setVoice] = useState<{ path: string; url: string } | null>(null);
  const [busy, setBusy] = useState(false);
  async function post() {
    if (!text.trim() && !voice) { toast.error('Write something or record a voice note'); return; }
    setBusy(true);
    try {
      await createReview(asset.id, { body: text.trim(), audio_path: voice?.path ?? null, timestamp_sec: null, mentioned_user_ids: mentions });
      toast.success('Note added; the team is notified');
      onPosted();
    } catch { toast.error('Could not add the note'); } finally { setBusy(false); }
  }
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl border border-slate-800 bg-bg-card p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-medium text-muted-strong text-sm">Note on <FileName name={asset.filename} className="font-mono" /></h3>
        <p className="text-xs text-muted mt-1 mb-3">Missing a shot, a clip to redo, an edit instruction. Type it or say it.</p>
        {/* The note reaches the whole team either way; naming someone is how you
            put it in front of the one person who has to act on it. */}
        <MentionTextarea value={text} onChange={setText} mentions={mentions} onMentionsChange={setMentions}
          rows={3} placeholder="What should the team know about this file? Type @ to name someone" />
        <div className="flex items-center justify-between gap-2 mt-3">
          <VoiceRecorder value={voice} onChange={setVoice} disabled={busy} />
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button size="sm" onClick={post} loading={busy} disabled={!text.trim() && !voice}>Add note</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

